import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Switch, IconButton, Tooltip, Button,
  TextField, Select, MenuItem, FormControl, InputLabel, Skeleton,
  Grid, Chip, Stack, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  Visibility as PreviewIcon, Image as ImageIcon, BrokenImageOutlined as BrokenImageIcon,
  Upload as UploadIcon, OpenInNew as OpenInNewIcon,
  DesktopWindows as DesktopIcon, PhoneIphone as MobileIcon,
  ViewCarousel as CarouselIcon, ViewModule as GridIcon,
  LocalOffer as OfferIcon, CategoryOutlined as CategoryIcon,
  Link as LinkIcon, ViewAgenda as LayoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { getImageUrl } from '../config';
import {
  PageHeader, Panel, EmptyState, ConfirmDialog, FormDialog, StatusBadge, Toolbar, useFeedback,
} from '../components/ui';
import { color, radius } from '../theme/tokens';

// ── Position Rich Metadata ─────────────────────────────────────
const SLOT_METADATA = {
  BD: {
    code: 'BD',
    tabLabel: 'Hero (Desktop)',
    icon: <DesktopIcon fontSize="small" />,
    pageName: 'Homepage',
    sectionName: 'Top Hero Slider (Desktop)',
    recommended_size: '1920×400px',
    aspect: '24 : 5',
    max_items: 4,
    description: 'Full-width rotating hero carousel at the top of the homepage on desktop screens. Auto-rotates every 5 seconds.',
    storefront_url: 'http://localhost:5173/#offers',
    badge: 'Desktop Hero',
  },
  BM: {
    code: 'BM',
    tabLabel: 'Hero (Mobile)',
    icon: <MobileIcon fontSize="small" />,
    pageName: 'Homepage',
    sectionName: 'Top Hero Slider (Mobile)',
    recommended_size: '768×300px',
    aspect: '16 : 9',
    max_items: 4,
    description: 'Paired mobile slides shown to smartphone visitors in the top hero carousel for maximum readability on small screens.',
    storefront_url: 'http://localhost:5173/#offers',
    badge: 'Mobile Hero',
  },
  SG: {
    code: 'SG',
    tabLabel: 'Promo Cards (Home)',
    icon: <GridIcon fontSize="small" />,
    pageName: 'Homepage',
    sectionName: 'Dual Promo Cards (Below Categories)',
    recommended_size: '600×300px',
    aspect: '2 : 1',
    max_items: 2,
    description: 'Two high-impact promo cards positioned directly below category circles on the homepage (e.g. Latest Launches & Accessories).',
    storefront_url: 'http://localhost:5173/',
    badge: 'Dual Cards',
  },
  HPB: {
    code: 'HPB',
    tabLabel: 'Deals & Spotlight',
    icon: <CarouselIcon fontSize="small" />,
    pageName: 'Homepage',
    sectionName: 'Deals & Flash Offer Carousel Strip',
    recommended_size: '1200×240px',
    aspect: '5 : 1',
    max_items: 2,
    description: 'Wide high-contrast promotional banner strip showcased between product shelves and the 90-minute delivery story.',
    storefront_url: 'http://localhost:5173/',
    badge: 'Deals Strip',
  },
  HPG: {
    code: 'HPG',
    tabLabel: 'Brand Showcase',
    icon: <OfferIcon fontSize="small" />,
    pageName: 'Homepage',
    sectionName: 'Featured Brand Spotlight Grid',
    recommended_size: '400×200px',
    aspect: '2 : 1',
    max_items: 4,
    description: 'Promotional tiles linking directly to brand-specific catalogs (Apple, Samsung, Vivo, OnePlus, Xiaomi).',
    storefront_url: 'http://localhost:5173/',
    badge: 'Brand Tiles',
  },
  HPO: {
    code: 'HPO',
    tabLabel: 'Category Banner',
    icon: <CategoryIcon fontSize="small" />,
    pageName: 'Category / Catalog Pages',
    sectionName: 'Product Catalog Top Banner',
    recommended_size: '1200×220px',
    aspect: '6 : 1',
    max_items: 2,
    description: 'Promotional banner displayed at the top of category pages (Smartphones, Accessories, Tablets) above the product grid.',
    storefront_url: 'http://localhost:5173/category/smartphones',
    badge: 'Catalog Banner',
  },
};

const EMPTY_FORM = {
  img_path: '', image_type: 'image', position: '',
  position_sq: 1, act_status: 1, itemcode: '', metaname: '', keyword: '',
};

// ── Interactive Visual Website Placement Wireframe ─────────────
const WebsitePlacementWireframe = ({ code }) => {
  const isMobile = code === 'BM';

  if (isMobile) {
    return (
      <Box
        sx={{
          width: 140,
          height: 195,
          bgcolor: '#0A0F1C',
          borderRadius: '18px',
          border: '3px solid #1E293B',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
          flexShrink: 0,
        }}
      >
        {/* Mobile speaker notch */}
        <Box sx={{ width: 28, height: 3, bgcolor: '#334155', borderRadius: 2, mx: 'auto', mb: 0.75 }} />

        {/* Mini header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Box sx={{ width: 14, height: 5, bgcolor: '#E3141E', borderRadius: 0.5 }} />
          <Box sx={{ width: 10, height: 4, bgcolor: '#334155', borderRadius: 0.5 }} />
        </Box>

        {/* ACTIVE MOBILE HERO BANNER */}
        <Box
          sx={{
            width: '100%',
            height: 52,
            bgcolor: '#E3141E',
            borderRadius: '6px',
            border: '2px solid #FECF05',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
            boxShadow: '0 0 10px rgba(227,20,30,0.5)',
            mb: 0.75,
            animation: 'pulse 2s infinite',
          }}
        >
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#FFFFFF', textAlign: 'center', lineHeight: 1.1 }}>
            ACTIVE ZONE
          </Typography>
          <Typography sx={{ fontSize: '0.45rem', fontWeight: 600, color: '#FECF05', textAlign: 'center' }}>
            Mobile Hero (768×300)
          </Typography>
        </Box>

        {/* Categories circles */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, justifyContent: 'center' }}>
          {[1, 2, 3, 4].map(i => (
            <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1E293B' }} />
          ))}
        </Box>

        {/* Product rows */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, flex: 1 }}>
          <Box sx={{ bgcolor: '#1E293B', borderRadius: '4px' }} />
          <Box sx={{ bgcolor: '#1E293B', borderRadius: '4px' }} />
        </Box>
      </Box>
    );
  }

  // Desktop Wireframe schematic
  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 260 },
        height: 175,
        bgcolor: '#FFFFFF',
        borderRadius: '10px',
        border: '1px solid #CBD5E1',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
        flexShrink: 0,
      }}
    >
      {/* Browser Chrome Header */}
      <Box sx={{ bgcolor: '#F1F5F9', px: 1, py: 0.6, borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#EF4444' }} />
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F59E0B' }} />
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
        <Box sx={{ flex: 1, height: 12, bgcolor: '#FFFFFF', borderRadius: '3px', border: '1px solid #E2E8F0', px: 0.5, display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.45rem', color: '#64748B', fontFamily: 'monospace' }}>
            hotspotstore.com
          </Typography>
        </Box>
      </Box>

      {/* Website Top Nav */}
      <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ width: 32, height: 6, bgcolor: '#E3141E', borderRadius: 0.5 }} />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Box sx={{ width: 28, height: 5, bgcolor: '#F1F5F9', borderRadius: 0.5 }} />
          <Box sx={{ width: 14, height: 5, bgcolor: '#F1F5F9', borderRadius: 0.5 }} />
        </Box>
      </Box>

      {/* Dynamic Page Content Wireframe */}
      <Box sx={{ p: 0.8, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.6, overflow: 'hidden' }}>
        {code === 'BD' ? (
          /* Active Desktop Hero */
          <Box
            sx={{
              height: 48,
              bgcolor: '#E3141E',
              borderRadius: '4px',
              border: '2px solid #FECF05',
              boxShadow: '0 0 10px rgba(227,20,30,0.4)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.1 }}>
              ACTIVE BANNER ZONE
            </Typography>
            <Typography sx={{ fontSize: '0.48rem', fontWeight: 600, color: '#FECF05' }}>
              Desktop Hero Carousel (1920×400)
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 20, bgcolor: '#F1F5F9', borderRadius: '3px', opacity: 0.5 }} />
        )}

        {/* Categories Bar */}
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'space-between' }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Box key={i} sx={{ width: 12, height: 6, bgcolor: '#E2E8F0', borderRadius: '2px' }} />
          ))}
        </Box>

        {code === 'SG' ? (
          /* Active Dual Promo Cards */
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.6 }}>
            {[1, 2].map(i => (
              <Box
                key={i}
                sx={{
                  height: 36,
                  bgcolor: '#E3141E',
                  borderRadius: '4px',
                  border: '1.5px solid #FECF05',
                  boxShadow: '0 0 8px rgba(227,20,30,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: '#FFFFFF' }}>
                  ACTIVE CARD #{i}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : code === 'HPB' ? (
          /* Active Deals Spotlight Strip */
          <Box
            sx={{
              height: 30,
              bgcolor: '#E3141E',
              borderRadius: '4px',
              border: '2px solid #FECF05',
              boxShadow: '0 0 10px rgba(227,20,30,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#FFFFFF' }}>
              ACTIVE DEALS STRIP (1200×240)
            </Typography>
          </Box>
        ) : code === 'HPG' ? (
          /* Active Brand Showcase Grid */
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.4 }}>
            {[1, 2, 3, 4].map(i => (
              <Box
                key={i}
                sx={{
                  height: 24,
                  bgcolor: '#E3141E',
                  borderRadius: '3px',
                  border: '1px solid #FECF05',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.45rem', fontWeight: 800, color: '#FFFFFF' }}>
                  BRAND {i}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : code === 'HPO' ? (
          /* Active Category Listing Banner */
          <Box
            sx={{
              height: 34,
              bgcolor: '#E3141E',
              borderRadius: '4px',
              border: '2px solid #FECF05',
              boxShadow: '0 0 10px rgba(227,20,30,0.4)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.1 }}>
              ACTIVE CATEGORY BANNER
            </Typography>
            <Typography sx={{ fontSize: '0.45rem', fontWeight: 600, color: '#FECF05' }}>
              Catalog Header (1200×220)
            </Typography>
          </Box>
        ) : (
          /* Neutral Products Shelf */
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, flex: 1 }}>
            <Box sx={{ bgcolor: '#F1F5F9', borderRadius: '3px' }} />
            <Box sx={{ bgcolor: '#F1F5F9', borderRadius: '3px' }} />
            <Box sx={{ bgcolor: '#F1F5F9', borderRadius: '3px' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Inline image/video with neutral fallback ──────────────────
const BannerImage = ({ src, alt, type = 'image' }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => { setFailed(false); }, [src]);

  const showFallback = failed || !src;
  const isVideo = type === 'video' || /\.(mp4|webm|mov|ogg)$/i.test(src || '');

  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: color.surfaceAlt, overflow: 'hidden', borderRadius: 1, border: `1px solid ${color.border}` }}>
      {!showFallback ? (
        isVideo ? (
          <Box
            component="video"
            src={getImageUrl(src)}
            controls
            autoPlay
            muted
            loop
            onError={() => setFailed(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <Box
            component="img"
            src={getImageUrl(src)}
            alt={alt || ''}
            onError={() => setFailed(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        )
      ) : (
        <Box
          sx={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            border: `1px dashed ${color.borderStrong}`, color: color.ink[400],
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 24 }} />
          <Typography variant="caption" sx={{ color: color.ink[400] }}>No preview</Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Banner form dialog ─────────────────────────────────────────
const BannerDialog = ({ open, onClose, banner, positions, defaultPosition, onSaved }) => {
  const feedback = useFeedback();
  const editing = !!banner;
  const [form, setForm]     = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (open) {
      setForm(banner ? {
        img_path:    banner.IMG_PATH    || '',
        image_type:  banner.IMAGE_TYPE  || 'image',
        position:    banner.POSITION    || defaultPosition || 'BD',
        position_sq: banner.POSITION_SQ || 1,
        act_status:  banner.ACT_STATUS  ?? 1,
        itemcode:    banner.ITEMCODE    || '',
        metaname:    banner.metaname    || '',
        keyword:     banner.keyword     || '',
      } : {
        ...EMPTY_FORM,
        position: defaultPosition || 'BD',
      });
      setError('');
    }
  }, [open, banner, defaultPosition]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const currentMeta = SLOT_METADATA[form.position] || {};

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setError('');

    try {
      const res = await api.post('/api/admin/upload/banner', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        set('img_path', res.data.url);
        const isVideo = /\.(mp4|webm|mov|ogg)$/i.test(file.name);
        set('image_type', isVideo ? 'video' : 'image');
        feedback.success('File uploaded successfully');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
      feedback.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.img_path.trim() || !form.position) {
      setError('Image URL and Position are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editing) await api.put(`/api/admin/banners/${banner.SRNO}`, form);
      else         await api.post('/api/admin/banners', form);
      feedback.success(editing ? 'Banner updated' : 'Banner added');
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Banner' : 'Add New Banner'}
      subtitle={editing ? 'Update the banner image and placement below.' : 'Add a banner to a customizable page slot.'}
      icon={<ImageIcon sx={{ fontSize: 18 }} />}
      maxWidth="sm"
      saving={saving}
      error={error}
      actions={
        <>
          <Button onClick={onClose} disabled={saving} variant="outlined" color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (editing ? 'Save Changes' : 'Add Banner')}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 0.5 }}>
        {/* Placement Breadcrumb Card */}
        <Box sx={{ p: 1.5, bgcolor: color.surfaceAlt, borderRadius: `${radius.md}px`, border: `1px solid ${color.border}` }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: color.ink[600], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            📍 Website Target Slot
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: color.primary[600], mt: 0.25 }}>
            {currentMeta.pageName || 'Homepage'} › {currentMeta.sectionName || form.position}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: color.ink[500], mt: 0.5 }}>
            Recommended Size: <strong>{currentMeta.recommended_size || '1920×400px'}</strong>
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Image/Video URL *"
              value={form.img_path}
              onChange={e => set('img_path', e.target.value)}
              placeholder="/uploads/banners/..."
              helperText="Enter URL or upload a file"
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              disabled={uploading}
              sx={{ height: 40, whiteSpace: 'nowrap' }}
            >
              Upload
              <input
                type="file"
                hidden
                accept="image/*,video/*"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>
        </Box>

        <BannerImage src={form.img_path} alt="preview" type={form.image_type} />

        <FormControl fullWidth size="small" required>
          <InputLabel>Placement Slot</InputLabel>
          <Select value={form.position} label="Placement Slot" onChange={e => set('position', e.target.value)}>
            {positions.map(p => {
              const meta = SLOT_METADATA[p.code] || {};
              return (
                <MenuItem key={p.code} value={p.code}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {meta.tabLabel || p.label || p.code} ({p.code})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {meta.pageName || 'Homepage'} · {meta.recommended_size || p.recommended_size}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Media Type</InputLabel>
          <Select value={form.image_type} label="Media Type" onChange={e => set('image_type', e.target.value)}>
            <MenuItem value="image">Static Image</MenuItem>
            <MenuItem value="video">MP4 Video</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size="small"
          type="number"
          label="Sequence / Slide Order"
          value={form.position_sq}
          onChange={e => set('position_sq', parseInt(e.target.value) || 1)}
          helperText="1 = First slide, 2 = Second slide, etc."
        />

        {/* Quick-Pick Target Destination */}
        <Box>
          <TextField
            fullWidth
            size="small"
            label="Destination Link / Product Item Code"
            value={form.itemcode}
            onChange={e => set('itemcode', e.target.value)}
            placeholder="/category/smartphones or item code"
            helperText="Where should this banner take customers when clicked?"
          />
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            <Typography variant="caption" sx={{ color: color.ink[500], alignSelf: 'center' }}>Presets:</Typography>
            {[
              { label: 'Smartphones', path: '/category/phones' },
              { label: 'Accessories', path: '/category/accessories' },
              { label: 'Audio', path: '/category/audio' },
              { label: 'Tablets', path: '/category/tablets' },
            ].map(preset => (
              <Chip
                key={preset.path}
                label={preset.label}
                size="small"
                clickable
                onClick={() => set('itemcode', preset.path)}
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            ))}
          </Box>
        </Box>

        <TextField
          fullWidth
          size="small"
          label="Banner Headline (metaname)"
          value={form.metaname}
          onChange={e => set('metaname', e.target.value)}
          placeholder="e.g. iPhone 18 Pro - Available Now"
        />

        <TextField
          fullWidth
          size="small"
          label="Subtext / Keywords (keyword)"
          value={form.keyword}
          onChange={e => set('keyword', e.target.value)}
          placeholder="e.g. Titanium. Built for Apple Intelligence"
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: color.surfaceAlt, borderRadius: `${radius.md}px` }}>
          <Switch checked={form.act_status === 1} onChange={e => set('act_status', e.target.checked ? 1 : 0)} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Active on Website</Typography>
            <Typography variant="caption" color="text.secondary">Customers can immediately see this banner live.</Typography>
          </Box>
        </Box>
      </Stack>
    </FormDialog>
  );
};

// ── Banner card ────────────────────────────────────────────────
const BannerCard = ({ banner, onEdit, onDelete, onToggle }) => {
  const meta = SLOT_METADATA[banner.POSITION] || {};
  const link = banner.ITEMCODE || '/category/phones';

  return (
    <Box
      sx={{
        bgcolor: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: `${radius.xl}px`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <BannerImage src={banner.IMG_PATH} alt={banner.metaname || banner.POSITION} />
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.75 }}>
          <StatusBadge
            label={banner.ACT_STATUS === 1 ? 'Live' : 'Draft'}
            tone={banner.ACT_STATUS === 1 ? 'success' : 'neutral'}
          />
          <StatusBadge label={meta.badge || `#${banner.POSITION_SQ}`} tone="neutral" dot={false} />
        </Box>
      </Box>

      <Box sx={{ px: 2, pt: 1.5, pb: 1, flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: color.ink[900], mb: 0.25 }} noWrap>
          {banner.metaname || banner.IMG_PATH?.split('/').pop() || 'Untitled Banner'}
        </Typography>
        {banner.keyword && (
          <Typography variant="caption" sx={{ color: color.ink[600], display: 'block', mb: 0.5 }} noWrap>
            {banner.keyword}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <LinkIcon sx={{ fontSize: 13, color: color.primary[600] }} />
          <Typography variant="caption" noWrap sx={{ color: color.ink[500], fontFamily: 'monospace' }}>
            {link}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: 1.5, py: 1,
          borderTop: `1px solid ${color.divider}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: color.surfaceAlt,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="Edit banner">
            <IconButton size="small" onClick={() => onEdit(banner)} aria-label="Edit banner">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Preview media">
            <span>
              <IconButton
                size="small"
                onClick={() => window.open(getImageUrl(banner.IMG_PATH), '_blank')}
                disabled={!banner.IMG_PATH}
                aria-label="Preview media"
              >
                <PreviewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(banner)} aria-label="Delete banner">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: banner.ACT_STATUS === 1 ? color.success.solid : color.ink[400], fontWeight: 600 }}>
            {banner.ACT_STATUS === 1 ? 'ACTIVE' : 'OFF'}
          </Typography>
          <Switch size="small" checked={banner.ACT_STATUS === 1} onChange={() => onToggle(banner)} />
        </Box>
      </Box>
    </Box>
  );
};

// ── Main Banners page ──────────────────────────────────────────
const Banners = () => {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const [banners, setBanners]       = useState([]);
  const [positions, setPositions]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState(0);
  const [formOpen, setFormOpen]     = useState(false);
  const [editBanner, setEditBanner] = useState(null);
  const [deleteBanner, setDeleteBanner] = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [inputVal, setInputVal]     = useState('');
  const [search, setSearch]         = useState('');

  const currentPosition = positions[activeTab] || {};
  const currentMeta = SLOT_METADATA[currentPosition.code] || {
    tabLabel: currentPosition.label || currentPosition.code,
    pageName: 'Storefront',
    sectionName: currentPosition.label,
    recommended_size: currentPosition.recommended_size || '1920×400px',
    description: 'Dynamic banner area displayed on the storefront.',
    storefront_url: 'http://localhost:5173/',
  };

  const fetchMetadata = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/banners/positions');
      setPositions(res.data.data);
    } catch {
      feedback.error('Failed to load banner positions');
    }
  }, [feedback]);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/banners');
      setBanners(res.data.data);
    } catch {
      feedback.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  }, [feedback]);

  useEffect(() => { fetchMetadata(); fetchBanners(); }, [fetchMetadata, fetchBanners]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(inputVal), 300);
    return () => clearTimeout(t);
  }, [inputVal]);

  const handleToggle = async (banner) => {
    try {
      await api.patch(`/api/admin/banners/${banner.SRNO}/toggle`);
      feedback.success('Banner status updated live');
      fetchBanners();
    } catch {
      feedback.error('Failed to update banner status');
    }
  };

  const handleDelete = async () => {
    if (!deleteBanner) return;
    setDeleting(true);
    try {
      await api.delete(`/api/admin/banners/${deleteBanner.SRNO}`);
      feedback.success('Banner deleted');
      fetchBanners();
      setDeleteBanner(null);
    } catch {
      feedback.error('Failed to delete banner');
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = (banner) => { setEditBanner(banner); setFormOpen(true); };
  const handleAdd = () => { setEditBanner(null); setFormOpen(true); };

  const positionBanners = banners.filter(b => b.POSITION === currentPosition.code);
  const activeCount = positionBanners.filter(b => b.ACT_STATUS === 1).length;
  const isFiltering = statusFilter !== 'all' || Boolean(search.trim());
  const filteredBanners = positionBanners.filter((b) => {
    const statusOk = statusFilter === 'all'
      || (statusFilter === 'active' ? b.ACT_STATUS === 1 : b.ACT_STATUS !== 1);
    const q = search.trim().toLowerCase();
    const textOk = !q || [b.IMG_PATH, b.ITEMCODE, b.metaname, b.keyword]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    return statusOk && textOk;
  });

  return (
    <Box>
      <PageHeader
        eyebrow="Marketing & CMS"
        title="Banners & Carousels"
        subtitle="Customize every banner, carousel, and promotional placement across the Hotspot storefront."
        actions={
          <>
            <Tooltip title="Refresh banners">
              <span>
                <IconButton onClick={fetchBanners} disabled={loading} aria-label="Refresh">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<LayoutIcon />}
              onClick={() => navigate('/homepage-layout')}
              sx={{ borderColor: color.primary[300], color: color.primary[600], fontWeight: 600 }}
            >
              Homepage Layout
            </Button>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(currentMeta.storefront_url || 'http://localhost:5173/', '_blank')}
            >
              View Live Page
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              Add banner
            </Button>
          </>
        }
      />

      {/* ── Tabs: Human-Readable Locations with Icons ───────── */}
      <Box sx={{ borderBottom: `1px solid ${color.border}`, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 52,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
            },
          }}
        >
          {positions.map((p) => {
            const count = banners.filter(b => b.POSITION === p.code).length;
            const meta = SLOT_METADATA[p.code] || {};
            return (
              <Tab
                key={p.code}
                icon={meta.icon || <CarouselIcon fontSize="small" />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{meta.tabLabel || p.label || p.code}</span>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: count > 0 ? color.primary[50] : color.ink[100],
                        color: count > 0 ? color.primary[600] : color.ink[500],
                      }}
                    />
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Box>

      {/* ── Interactive Website Placement Guide & Mini Wireframe ── */}
      <Box
        sx={{
          bgcolor: color.surface,
          border: `1px solid ${color.border}`,
          borderRadius: `${radius.xl}px`,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          boxShadow: '0 2px 6px rgba(15,23,42,0.04)',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`📍 Page: ${currentMeta.pageName || 'Homepage'}`}
              size="small"
              sx={{ fontWeight: 700, bgcolor: color.primary[50], color: color.primary[600] }}
            />
            <Chip
              label={currentMeta.sectionName || currentPosition.code}
              size="small"
              sx={{ fontWeight: 600, bgcolor: color.surfaceAlt, color: color.ink[700] }}
            />
            <Chip
              label={`${activeCount} of ${positionBanners.length} Live`}
              size="small"
              color={activeCount > 0 ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>

          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: color.ink[900], mb: 0.5 }}>
            {currentMeta.tabLabel} ({currentPosition.code})
          </Typography>

          <Typography sx={{ fontSize: '0.875rem', color: color.ink[600], mb: 2, lineHeight: 1.5 }}>
            {currentMeta.description}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: color.ink[500] }}>
              Recommended: <strong>{currentMeta.recommended_size || currentPosition.recommended_size}</strong> (Aspect: {currentMeta.aspect || 'Free'})
            </Typography>
            <Typography variant="caption" sx={{ color: color.ink[500] }}>
              Slot Capacity: <strong>{positionBanners.length} / {currentMeta.max_items || currentPosition.max_items || 2} Banners</strong>
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => window.open(currentMeta.storefront_url || 'http://localhost:5173/', '_blank')}
              sx={{ color: color.primary[600], fontWeight: 600, p: 0 }}
            >
              Open Live Page ↗
            </Button>
          </Box>
        </Box>

        {/* Visual Miniature Layout Mockup */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
          <WebsitePlacementWireframe code={currentPosition.code} />
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: color.ink[500], fontWeight: 600, textTransform: 'uppercase' }}>
            Interactive Wireframe Placement
          </Typography>
        </Box>
      </Box>

      {/* ── Banners Management Panel ───────────────────────── */}
      <Panel
        noPadding
        title={`${currentMeta.tabLabel} Banners`}
        subtitle={`${filteredBanners.length} banners in this placement slot`}
        actions={
          <StatusBadge
            label={`${activeCount} Active / ${filteredBanners.length} Total`}
            tone={activeCount > 0 ? 'success' : 'neutral'}
          />
        }
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ mb: 2.5 }}>
            <Toolbar
              search={inputVal}
              onSearch={setInputVal}
              searchPlaceholder="Search by title, item code, or file name..."
              onRefresh={fetchBanners}
              refreshing={loading}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="banner-status-label">Status</InputLabel>
                <Select
                  labelId="banner-status-label"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="active">Active only</MenuItem>
                  <MenuItem value="inactive">Inactive only</MenuItem>
                </Select>
              </FormControl>
            </Toolbar>
          </Box>

          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rounded" height={240} />
                </Grid>
              ))}
            </Grid>
          ) : filteredBanners.length === 0 ? (
            <EmptyState
              icon={<ImageIcon sx={{ fontSize: 26 }} />}
              title={isFiltering
                ? `No matching banners in ${currentPosition.code}`
                : `No banners configured for ${currentMeta.tabLabel}`}
              description={isFiltering
                ? 'Try adjusting your search query or status filter.'
                : `Upload a banner sized ${currentMeta.recommended_size} to showcase in this section on the live storefront.`}
              actionLabel="Add banner to this slot"
              onAction={handleAdd}
            />
          ) : (
            <Grid container spacing={2.5}>
              {filteredBanners.map(banner => (
                <Grid key={banner.SRNO} size={{ xs: 12, sm: 6, md: 4 }}>
                  <BannerCard
                    banner={banner}
                    onEdit={handleEdit}
                    onDelete={setDeleteBanner}
                    onToggle={handleToggle}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Panel>

      <BannerDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        banner={editBanner}
        positions={positions}
        defaultPosition={currentPosition.code}
        onSaved={fetchBanners}
      />

      <ConfirmDialog
        open={!!deleteBanner}
        onClose={() => setDeleteBanner(null)}
        onConfirm={handleDelete}
        title="Delete banner?"
        description={
          <>
            This will permanently remove the banner from{' '}
            <strong>{SLOT_METADATA[deleteBanner?.POSITION]?.tabLabel || deleteBanner?.POSITION}</strong>. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
      />
    </Box>
  );
};

export default Banners;
